import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Cable, Loader2, ExternalLink, ShieldCheck, KeyRound, RefreshCw, CheckCircle2, XCircle, Calendar, PlayCircle, AlertTriangle, AlertCircle } from "lucide-react";
import api from "@/lib/api";

const inp = "w-full bg-card border border-border rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-[#3B82F6]/30 font-mono";
const btn = "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold transition-all";

const SCOPES = [
  "ZohoCRM.org.READ",
  "ZohoCRM.modules.ALL",
  "ZohoCRM.settings.fields.READ",
  "zohobookings.data.CREATE",
  "zohobookings.data.READ",
];

export default function ZohoSettings() {
  const [cfg, setCfg] = useState(null);
  const [regions, setRegions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnoseReport, setDiagnoseReport] = useState(null);
  const [code, setCode] = useState("");
  const [exchanging, setExchanging] = useState(false);
  // Zoho Bookings — workspaces / services / staff selectors
  const [bkLoading, setBkLoading] = useState(false);
  const [bkWorkspaces, setBkWorkspaces] = useState([]);
  const [bkServices, setBkServices] = useState([]);
  const [bkStaffs, setBkStaffs] = useState([]);
  const [bkTest, setBkTest] = useState({ scheduled_at: "", staff_id: "" });
  const [bkTesting, setBkTesting] = useState(false);
  const [bkTestResult, setBkTestResult] = useState(null);
  const [bkScopeMissing, setBkScopeMissing] = useState(false);
  const [caringManagers, setCaringManagers] = useState([]);

  const load = async () => {
    const [c, r, l, u] = await Promise.all([
      api.get("/zoho/config"),
      api.get("/zoho/regions"),
      api.get("/zoho/logs"),
      api.get("/users"),
    ]);
    setCfg(c.data); setRegions(r.data); setLogs(l.data);
    // Only Caring managers/admins participate in QT scheduling.
    setCaringManagers((u.data || []).filter(x => (x.role === "gestor" || x.role === "admin") && x.active !== false));
  };
  useEffect(() => { load(); }, []);

  // Auto-load workspaces / services / staff lists when Bookings is already
  // configured (nice for editing an existing setup without re-fetching manually).
  useEffect(() => {
    if (cfg?.refresh_token && cfg?.bookings_enabled && bkWorkspaces.length === 0) {
      loadBookingsWorkspaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.refresh_token, cfg?.bookings_enabled]);
  useEffect(() => {
    if (cfg?.bookings_workspace_id) {
      loadBookingsServices(cfg.bookings_workspace_id);
      // Load ALL staff of the workspace (not filtered by service) so the admin
      // can pick any Caring manager as fallback even if that staff isn't yet
      // assigned to the QT service in the Zoho Bookings admin console.
      loadBookingsStaffs(cfg.bookings_workspace_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.bookings_workspace_id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/zoho/config", cfg);
      setCfg(data);
      toast.success("Configuração Zoho guardada");
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); } finally { setSaving(false); }
  };

  const exchangeCode = async () => {
    if (!code.trim()) { toast.error("Cole o código gerado no Zoho API Console"); return; }
    setExchanging(true);
    try {
      const { data } = await api.post("/zoho/exchange-code", { code: code.trim() });
      toast.success("Refresh token obtido com sucesso");
      setCode("");
      await load();
    } catch(e){ toast.error(e.response?.data?.detail || "Falha na troca do código"); }
    finally { setExchanging(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/zoho/test-connection");
      setTestResult({ ok: true, ...data });
      toast.success(`Ligado a ${data.company || "Zoho CRM"}`);
      await load();
    } catch(e){
      setTestResult({ ok: false, error: e.response?.data?.detail || "Falha" });
      toast.error(e.response?.data?.detail || "Falha");
    } finally { setTesting(false); }
  };

  const runDiagnose = async () => {
    setDiagnosing(true); setDiagnoseReport(null);
    try {
      const { data } = await api.get("/zoho/diagnose");
      setDiagnoseReport(data);
      const failed = (data.steps || []).filter(s => s.status === "error").length;
      if (failed === 0) toast.success("Diagnóstico: todos os passos OK ✓");
      else toast.error(`Diagnóstico: ${failed} passo(s) com erro — ver painel abaixo`);
    } catch(e) {
      toast.error(e.response?.data?.detail || "Falha no diagnóstico");
    } finally { setDiagnosing(false); }
  };

  // Zoho Bookings helpers ---------------------------------------------------
  const _isScopeMissing = (err) => {
    const d = err?.response?.data;
    if (!d) return false;
    // Prefer the raw structured payload preserved by the axios interceptor
    // (the normal `detail` field is stringified before we get here).
    const raw = d.detail_raw;
    if (raw && typeof raw === "object" && raw.code === "BOOKINGS_SCOPE_MISSING") return true;
    if (typeof d === "object" && (d.code === "BOOKINGS_SCOPE_MISSING" || d.detail?.code === "BOOKINGS_SCOPE_MISSING")) return true;
    // FastAPI wraps HTTPException detail into { detail: <body> }
    return d.detail && typeof d.detail === "object" && d.detail.code === "BOOKINGS_SCOPE_MISSING";
  };
  const _scopeMissingMessage = (err) => {
    const raw = err?.response?.data?.detail_raw;
    if (raw && typeof raw === "object" && raw.message) return raw.message;
    const d = err?.response?.data?.detail;
    if (d && typeof d === "object" && d.message) return d.message;
    if (typeof d === "string" && d) return d;
    return "Refresh_token sem scopes de Zoho Bookings. Refaça o passo 2 do wizard OAuth.";
  };
  const loadBookingsWorkspaces = async () => {
    setBkLoading(true);
    try {
      const { data } = await api.get("/zoho/bookings/workspaces");
      setBkWorkspaces(data.items || []);
      setBkScopeMissing(false);
      toast.success(`${(data.items || []).length} workspace(s) encontrado(s)`);
    } catch (e) {
      if (_isScopeMissing(e)) {
        setBkScopeMissing(true);
        toast.error("Zoho Bookings sem permissão — refaça o passo 2 do wizard OAuth");
      } else {
        toast.error(e.response?.data?.detail || e.response?.data?.detail?.message || "Erro ao listar workspaces");
      }
    } finally { setBkLoading(false); }
  };
  const loadBookingsServices = async (workspaceId) => {
    if (!workspaceId) { setBkServices([]); return; }
    setBkLoading(true);
    try {
      const { data } = await api.get("/zoho/bookings/services", { params: { workspace_id: workspaceId } });
      setBkServices(data.items || []);
      setBkScopeMissing(false);
    } catch (e) {
      if (_isScopeMissing(e)) { setBkScopeMissing(true); }
      else { toast.error(e.response?.data?.detail || "Erro ao listar services"); }
    } finally { setBkLoading(false); }
  };
  const loadBookingsStaffs = async (workspaceId, serviceId) => {
    if (!workspaceId) { setBkStaffs([]); return; }
    setBkLoading(true);
    try {
      const { data } = await api.get("/zoho/bookings/staffs", { params: { workspace_id: workspaceId, service_id: serviceId || undefined } });
      setBkStaffs(data.items || []);
      setBkScopeMissing(false);
    } catch (e) {
      if (_isScopeMissing(e)) { setBkScopeMissing(true); }
      else { toast.error(e.response?.data?.detail || "Erro ao listar staff"); }
    } finally { setBkLoading(false); }
  };
  const testBooking = async () => {
    if (!bkTest.scheduled_at) { toast.error("Escolha data/hora do teste"); return; }
    setBkTesting(true); setBkTestResult(null);
    try {
      const { data } = await api.post("/zoho/bookings/test", {
        scheduled_at: bkTest.scheduled_at,
        staff_id: bkTest.staff_id || undefined,
      });
      const rv = (data?.response?.returnvalue) || {};
      setBkTestResult({ ok: true, booking_id: rv.booking_id || rv.id || "(sem id)", raw: rv });
      toast.success("Agendamento de teste criado no Zoho Bookings");
    } catch (e) {
      if (_isScopeMissing(e)) {
        setBkScopeMissing(true);
        setBkTestResult({ ok: false, error: _scopeMissingMessage(e), scope_missing: true });
      } else {
        const msg = e.response?.data?.detail || "Falha no teste";
        setBkTestResult({ ok: false, error: typeof msg === "string" ? msg : JSON.stringify(msg) });
      }
    } finally { setBkTesting(false); }
  };

  if (!cfg) return <div className="p-10 text-muted-foreground">A carregar...</div>;

  const region = regions.find(r => r.key === cfg.region) || regions[0];
  const scopesQS = encodeURIComponent(SCOPES.join(","));
  const consoleUrl = "https://api-console.zoho.com/";

  const hasCreds = !!(cfg.client_id && cfg.client_secret);
  const hasRefresh = !!cfg.refresh_token;

  return (
    <div className="p-8 lg:p-12 max-w-4xl mx-auto space-y-6" data-testid="zoho-settings-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Integração</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-foreground">Zoho CRM</h1>
        <p className="text-muted-foreground text-sm mt-2">Sincronize Contas, Notas e Tarefas automaticamente com o Zoho CRM v8.</p>
      </div>

      {/* Step 1 — Credentials */}
      <div className="bg-card border border-border rounded-2xl p-8 space-y-5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-[11px] font-semibold text-foreground">1</div>
          <h3 className="font-ui text-lg">Credenciais</h3>
          {hasCreds && <CheckCircle2 className="w-4 h-4 text-[#059669]" />}
        </div>
        <p className="text-xs text-muted-foreground">
          Vá a <a href={consoleUrl} target="_blank" rel="noreferrer" className="text-foreground underline inline-flex items-center gap-1">Zoho API Console <ExternalLink className="w-3 h-3" /></a> → Get Started → <b>Self Client</b> → Create Now → separador <b>Client Secret</b>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Região</div>
            <select data-testid="zoho-region" className={inp} value={cfg.region} onChange={(e)=>setCfg({...cfg, region:e.target.value})}>
              {regions.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Client ID</div>
            <input data-testid="zoho-client-id" className={inp} placeholder="1000.xxxxx" value={cfg.client_id||""} onChange={(e)=>setCfg({...cfg, client_id:e.target.value})} />
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Client Secret</div>
            <input data-testid="zoho-client-secret" type="password" placeholder="••••••••" className={inp} value={cfg.client_secret||""} onChange={(e)=>setCfg({...cfg, client_secret:e.target.value})} />
          </label>
        </div>
        <div className="flex justify-end">
          <button data-testid="zoho-save" onClick={save} disabled={saving} className={`${btn} bg-foreground hover:bg-slate-800 text-background`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar
          </button>
        </div>
      </div>

      {/* Step 2 — Authorization code */}
      <div className={`bg-card border border-border rounded-2xl p-8 space-y-5 ${!hasCreds ? "opacity-60 pointer-events-none" : ""}`}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-[11px] font-semibold text-foreground">2</div>
          <h3 className="font-ui text-lg">Gerar refresh token</h3>
          {hasRefresh && <CheckCircle2 className="w-4 h-4 text-[#059669]" />}
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Ainda no <b>Self Client</b>, abra o separador <b>Generate Code</b>. Cole os <b>scopes</b> abaixo, escolha uma validade (10 minutos chega), gere e cole o código aqui.
        </p>
        <div className="rounded-lg bg-muted border border-border p-4 text-xs">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground/70 mb-2">Scopes a colar</div>
          <div className="font-mono text-foreground break-all">{SCOPES.join(",")}</div>
          <button
            type="button"
            onClick={() => { navigator.clipboard.writeText(SCOPES.join(",")); toast.success("Scopes copiados"); }}
            className="mt-2 text-[10px] uppercase tracking-[0.15em] text-foreground hover:text-foreground"
            data-testid="copy-scopes"
          >
            Copiar
          </button>
        </div>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Código de autorização (válido ~10 min)</div>
          <input data-testid="zoho-code" className={inp} placeholder="1000.xxxxxxxxxxxxxxxx.xxxxxxxxxxxxxxxx" value={code} onChange={(e)=>setCode(e.target.value)} />
        </label>
        <div className="flex justify-end">
          <button data-testid="zoho-exchange" onClick={exchangeCode} disabled={exchanging || !code.trim()} className={`${btn} bg-foreground hover:bg-foreground text-background disabled:opacity-40`}>
            {exchanging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Trocar por refresh token
          </button>
        </div>
        {hasRefresh && (
          <div className="text-[11px] text-[#059669] inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> Refresh token guardado {cfg.api_domain ? `· api_domain: ${cfg.api_domain}` : ""}
          </div>
        )}
      </div>

      {/* Step 3 — Test connection + enable */}
      <div className={`bg-card border border-border rounded-2xl p-8 space-y-5 ${!hasRefresh ? "opacity-60 pointer-events-none" : ""}`}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-[11px] font-semibold text-foreground">3</div>
          <h3 className="font-ui text-lg">Testar & ativar</h3>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="zoho-test" onClick={testConnection} disabled={testing} className={`${btn} bg-muted hover:bg-muted border border-border text-muted-foreground`}>
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Testar ligação
          </button>
          <button data-testid="zoho-diagnose" onClick={runDiagnose} disabled={diagnosing} className={`${btn} bg-[#1F2937] hover:bg-[#111827] text-primary-foreground`}>
            {diagnosing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertCircle className="w-3.5 h-3.5" />} Diagnosticar Zoho
          </button>
          <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input data-testid="zoho-enabled" type="checkbox" checked={!!cfg.enabled} onChange={(e)=>setCfg({...cfg, enabled:e.target.checked})} className="accent-[#1D4ED8]" />
            Ativar sincronização automática
          </label>
          <button data-testid="zoho-save-2" onClick={save} disabled={saving} className={`${btn} bg-foreground hover:bg-slate-800 text-background ml-auto`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar
          </button>
        </div>
        {testResult && (
          <div className={`text-xs rounded-lg p-3 border ${testResult.ok ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] text-[#059669]" : "border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]"}`} data-testid="zoho-test-result">
            {testResult.ok ? (
              <div className="space-y-0.5">
                <div className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Ligação estabelecida</div>
                <div><b>Empresa:</b> {testResult.company || "—"}</div>
                <div><b>Email:</b> {testResult.primary_email || "—"} · <b>País:</b> {testResult.country || "—"} · <b>TZ:</b> {testResult.time_zone || "—"}</div>
              </div>
            ) : (
              <div className="flex items-start gap-1.5"><XCircle className="w-3.5 h-3.5 mt-0.5" /> <span className="break-all">{testResult.error}</span></div>
            )}
          </div>
        )}
        {diagnoseReport && <DiagnoseReportView report={diagnoseReport} />}

        <div className="pt-4 border-t border-border/60">
          <div className="text-[10px] uppercase tracking-[0.15em] text-foreground font-semibold mb-3">Importação automática de Leads do Zoho</div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input data-testid="zoho-auto-import" type="checkbox" checked={!!cfg.auto_import_enabled} onChange={(e)=>setCfg({...cfg, auto_import_enabled:e.target.checked})} className="accent-[#1D4ED8]" />
              Importar automaticamente
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              Intervalo:
              <input data-testid="zoho-interval" type="number" min={1} max={120} value={cfg.auto_import_interval_minutes ?? 5}
                onChange={(e)=>setCfg({...cfg, auto_import_interval_minutes: Math.max(1, parseInt(e.target.value)||5)})}
                className="w-20 bg-card border border-border rounded-lg px-2 py-1 text-sm" /> min
            </label>
            <button
              data-testid="zoho-import-now"
              onClick={async () => {
                try {
                  const { data } = await api.post("/zoho/import");
                  toast.success(`Importação: ${data.imported} novas · ${data.updated} atualizadas`);
                  await load();
                } catch(e){ toast.error(e.response?.data?.detail || "Falha na importação"); }
              }}
              className={`${btn} bg-foreground hover:bg-foreground text-background ml-auto`}
            >
              <RefreshCw className="w-3.5 h-3.5" /> Importar agora
            </button>
            <button
              data-testid="zoho-import-full"
              onClick={async () => {
                if (!window.confirm("Importação COMPLETA vai reler TODOS os leads do Zoho (não apenas os modificados). Pode demorar alguns minutos e envolver várias milhares de leads. Continuar?")) return;
                try {
                  // Pass force_full both via URL and axios params to eliminate any
                  // possibility of the query string being stripped by middleware.
                  const { data } = await api.post("/zoho/import?force_full=true", null, {
                    params: { force_full: true },
                  });
                  const seen = data.rows_seen ?? (data.imported + data.updated);
                  const ff = data.force_full === true ? "" : " (ATENÇÃO: force_full=false — query param perdido!)";
                  toast.success(`Importação completa: ${data.imported} novas · ${data.updated} atualizadas · ${seen} lidas do Zoho${ff}`,
                    { duration: 8000 });
                  if (seen === 0 && data.force_full === true) {
                    toast.error("Zoho devolveu 0 leads mesmo com force_full=true. Verifica: (1) scope OAuth 'ZohoCRM.modules.leads.READ' na configuração, (2) permissões do módulo Leads para o utilizador OAuth, (3) reautoriza a app em /settings/zoho.",
                      { duration: 15000 });
                  }
                  await load();
                } catch(e){ toast.error(e.response?.data?.detail || "Falha na importação completa"); }
              }}
              className={`${btn} bg-foreground hover:bg-[#061729] text-background`}
              title="Ignora o watermark e reimporta todos os leads — útil para backfill de campos novos (ex.: sincronizar Lead_Status pela primeira vez)."
            >
              <RefreshCw className="w-3.5 h-3.5" /> Importação completa
            </button>
          </div>
          {cfg.last_import_at && (
            <div className="text-[11px] text-muted-foreground mt-3">
              Última importação: {new Date(cfg.last_import_at).toLocaleString('pt-PT')} · {cfg.last_import_count} registos
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-border/60" data-testid="lead-status-field-block">
          <div className="text-[10px] uppercase tracking-[0.15em] text-foreground font-semibold mb-1">
            Campo Zoho para "Estado do Lead"
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Alguns tenants renomeiam o campo padrão <code>Lead_Status</code> para algo como
            "Estado de Cliente Potencial". Escolha aqui qual dos picklists do módulo
            <b> Clientes potenciais</b> representa o estado do lead — só assim conseguimos
            espelhar o valor correcto na coluna <b>ESTADO</b> da lista.
          </p>
          <LeadStatusFieldPicker cfg={cfg} onSaved={load} />
        </div>
      </div>

      {/* Step 4 — Zoho Bookings (agendamento QT) — MARCADO COMO LEGADO */}
      <div className={`bg-card border border-border rounded-2xl p-8 space-y-5 ${!hasRefresh ? "opacity-60 pointer-events-none" : ""}`} data-testid="bookings-block">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-[11px] font-semibold text-foreground">4</div>
          <h3 className="font-ui text-lg inline-flex items-center gap-2"><Calendar className="w-4 h-4 text-foreground" /> Zoho Bookings <span className="text-[10px] uppercase tracking-widest bg-muted text-foreground border border-border rounded-full px-2 py-0.5">legado</span></h3>
        </div>
        <div className="rounded-lg border-2 border-[hsl(var(--info))]/30 bg-[hsl(var(--info-bg))] p-4 space-y-2" data-testid="teams-migration-banner">
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[hsl(var(--info))]">
            Migrado para Microsoft Teams
          </div>
          <p className="text-xs text-foreground leading-relaxed">
            O agendamento da Reunião Técnica passou a criar automaticamente um evento no <b>calendário Outlook do gestor</b> com <b>link Teams gerado pelo Microsoft Graph</b>. O cliente recebe o convite <code>.ics</code> nativo com o link Teams. Este bloco Zoho Bookings mantém-se apenas para consulta histórica — não é usado em novos agendamentos.
          </p>
          <p className="text-[11px] text-[hsl(var(--info))] leading-relaxed">
            Requer que a app Azure AD (a mesma dos emails) tenha os scopes <code>Calendars.ReadWrite</code> concedidos pelo admin da tenant.
          </p>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Quando o gestor marca a Qualificação Técnica no lead, a Caring cria automaticamente uma reunião no <b>Zoho Bookings</b> — na agenda do gestor certo. Se o gestor tiver um <code>zoho_staff_id</code> definido no perfil, é essa a agenda usada; senão cai para o <b>staff default</b> abaixo.
        </p>

        {bkScopeMissing && (
          <div className="rounded-xl border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger-bg))] p-4 flex items-start gap-3" data-testid="bookings-scope-missing">
            <XCircle className="w-5 h-5 text-[hsl(var(--danger))] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-[hsl(var(--danger))] mb-1">Refresh_token sem scopes de Zoho Bookings</div>
              <div className="text-xs text-[hsl(var(--danger))] leading-relaxed mb-3">
                O token OAuth atual foi gerado sem permissão para o Zoho Bookings. Refaça o <b>Passo 2 — Autorização</b> (em cima nesta página) para gerar um novo <code>refresh_token</code> com os scopes de Bookings. Os scopes necessários já estão listados no passo 2 (<code>zohobookings.data.CREATE</code> + <code>zohobookings.data.READ</code>).
              </div>
              <button
                onClick={() => { window.scrollTo({top: 0, behavior: "smooth"}); }}
                className={`${btn} bg-red-600 hover:bg-red-700 text-primary-foreground`}
                data-testid="bookings-scope-goto-step2">
                <RefreshCw className="w-3.5 h-3.5" /> Ir ao Passo 2 (Autorização)
              </button>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="bookings-enabled"
              checked={!!cfg.bookings_enabled}
              onChange={e => setCfg({ ...cfg, bookings_enabled: e.target.checked })} />
            <span>Ativar integração Zoho Bookings</span>
          </label>
          <button onClick={loadBookingsWorkspaces} disabled={bkLoading}
            className={`${btn} bg-card border border-border text-muted-foreground hover:bg-muted/40 ml-auto`}
            data-testid="bookings-refresh-workspaces">
            {bkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Ler workspaces do Zoho
          </button>
        </div>

        {cfg.bookings_enabled && (
          <div className="space-y-4 pt-3 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">Workspace</div>
                <select data-testid="bookings-workspace"
                  className={inp}
                  value={cfg.bookings_workspace_id || ""}
                  onChange={e => {
                    const next = e.target.value;
                    if (next === cfg.bookings_workspace_id) return; // no-op: user re-clicked same workspace
                    setCfg({ ...cfg, bookings_workspace_id: next, bookings_service_id: "", bookings_default_staff_id: "" });
                  }}>
                  <option value="">— Escolher workspace —</option>
                  {bkWorkspaces.map(w => (
                    <option key={w.id || w.workspace_id} value={w.id || w.workspace_id}>
                      {w.name || w.workspace_name || w.id || w.workspace_id}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">Service (tipo de reunião QT)</div>
                <select data-testid="bookings-service"
                  className={inp} disabled={!cfg.bookings_workspace_id}
                  value={cfg.bookings_service_id || ""}
                  onChange={e => setCfg({ ...cfg, bookings_service_id: e.target.value })}>
                  <option value="">— Escolher service —</option>
                  {bkServices.map(s => (
                    <option key={s.id || s.service_id} value={s.id || s.service_id}>
                      {s.name || s.service_name || s.id}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">Staff default (fallback)</div>
                <select data-testid="bookings-default-staff"
                  className={inp} disabled={!cfg.bookings_workspace_id}
                  value={cfg.bookings_default_staff_id || ""}
                  onChange={e => setCfg({ ...cfg, bookings_default_staff_id: e.target.value })}>
                  <option value="">— Nenhum (obrigar mapeamento por gestor) —</option>
                  {bkStaffs.map(s => {
                    const displayName = s.name
                      || `${s.first_name || ""} ${s.last_name || ""}`.trim()
                      || s.email
                      || s.id
                      || s.staff_id;
                    return (
                      <option key={s.id || s.staff_id} value={s.id || s.staff_id}>
                        {displayName}{s.email && !displayName.includes(s.email) ? ` · ${s.email}` : ""}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">Timezone</div>
                <input data-testid="bookings-tz" className={inp}
                  value={cfg.bookings_timezone || ""}
                  onChange={e => setCfg({ ...cfg, bookings_timezone: e.target.value })}
                  placeholder="Europe/Lisbon" />
              </label>
            </div>
            <div className="text-[11px] text-foreground bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/20 rounded-lg p-3 leading-relaxed">
              💡 Para cada gestor da Caring que deva ter agenda própria, edite o utilizador em <b>Configurações → Utilizadores</b> e preencha o campo <b>Zoho Staff ID</b> com o ID correspondente na lista acima. Sem esse mapeamento, todas as reuniões cairão no <i>staff default</i>.
            </div>

            {/* Diagnóstico — mostra exactamente o que o backend leu e como cada
                Caring manager está mapeado ao Zoho. Torna óbvio se algum gestor
                não tem Zoho Staff ID atribuído (razão comum para "não aparece"). */}
            <div className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="bookings-diagnostic">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-semibold text-primary">
                <ShieldCheck className="w-3.5 h-3.5" /> Diagnóstico da configuração
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
                <div className="rounded border border-border px-3 py-2">
                  <div className="text-[9px] text-muted-foreground uppercase font-semibold">Workspace ID guardado</div>
                  <div className="font-mono text-foreground break-all" data-testid="diag-workspace-id">{cfg.bookings_workspace_id || "— vazio —"}</div>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <div className="text-[9px] text-muted-foreground uppercase font-semibold">Service ID guardado</div>
                  <div className="font-mono text-foreground break-all" data-testid="diag-service-id">{cfg.bookings_service_id || "— vazio —"}</div>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <div className="text-[9px] text-muted-foreground uppercase font-semibold">Staff default guardado</div>
                  <div className="font-mono text-foreground break-all" data-testid="diag-staff-id">{cfg.bookings_default_staff_id || "— vazio —"}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded border border-border px-3 py-2">
                  <div className="text-[9px] text-muted-foreground uppercase font-semibold">Staff lidos do Zoho</div>
                  <div className="text-foreground" data-testid="diag-zoho-staff-count">
                    <b>{bkStaffs.length}</b> pessoa(s) devolvidas pela API Zoho
                  </div>
                </div>
                <div className="rounded border border-border px-3 py-2">
                  <div className="text-[9px] text-muted-foreground uppercase font-semibold">Gestores Caring ativos</div>
                  <div className="text-foreground" data-testid="diag-caring-count">
                    <b>{caringManagers.length}</b> com role gestor/admin
                  </div>
                </div>
              </div>
              {/* Mapping table — Caring manager → zoho_staff_id */}
              <div className="rounded border border-border overflow-hidden">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold px-3 py-2 bg-muted/40 border-b border-border">
                  Mapeamento gestor Caring → Zoho Staff ID
                </div>
                <table className="w-full text-[11px]">
                  <thead className="bg-card border-b border-border text-muted-foreground">
                    <tr>
                      <th className="text-left px-3 py-1.5 font-semibold">Gestor Caring</th>
                      <th className="text-left px-3 py-1.5 font-semibold">Email</th>
                      <th className="text-left px-3 py-1.5 font-semibold">Zoho Staff ID</th>
                      <th className="text-left px-3 py-1.5 font-semibold">Estado</th>
                    </tr>
                  </thead>
                  <tbody data-testid="diag-mapping-tbody">
                    {caringManagers.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-3 text-muted-foreground">Nenhum gestor Caring ativo encontrado.</td></tr>
                    )}
                    {caringManagers.map(m => {
                      const mapped = !!m.zoho_staff_id;
                      const matchesZoho = mapped && bkStaffs.some(s => (s.id || s.staff_id) === m.zoho_staff_id);
                      return (
                        <tr key={m.id} className="border-b border-[#F1EEE6] last:border-0">
                          <td className="px-3 py-1.5 text-foreground">{m.name} <span className="text-muted-foreground">({m.role})</span></td>
                          <td className="px-3 py-1.5 text-muted-foreground">{m.email}</td>
                          <td className="px-3 py-1.5 font-mono text-foreground">{m.zoho_staff_id || <span className="text-[hsl(var(--danger))]">— não mapeado —</span>}</td>
                          <td className="px-3 py-1.5">
                            {!mapped ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 rounded-full px-2 py-0.5">
                                <AlertTriangle className="w-3 h-3" /> Sem staff Zoho
                              </span>
                            ) : matchesZoho ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[hsl(var(--success))] bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/25 rounded-full px-2 py-0.5">
                                <CheckCircle2 className="w-3 h-3" /> OK
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/25 rounded-full px-2 py-0.5" title="ID está preenchido mas não corresponde a nenhum staff devolvido agora pelo Zoho — pode estar inativo ou o service filtra">
                                <AlertTriangle className="w-3 h-3" /> ID não corresponde
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] text-muted-foreground leading-relaxed">
                Gestores sem <b>Zoho Staff ID</b> não têm agenda própria — todos os seus agendamentos cairão no <i>Staff default</i> acima.
                Corrija em <a href="/settings/users" className="text-primary underline">Configurações → Utilizadores</a>.
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button onClick={save} disabled={saving} className={`${btn} bg-foreground hover:bg-slate-800 text-background`} data-testid="bookings-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar Bookings
              </button>
            </div>

            {/* Test appointment */}
            <div className="pt-4 border-t border-border space-y-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Testar agendamento</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input data-testid="bookings-test-datetime" type="datetime-local" className={inp}
                  value={bkTest.scheduled_at} onChange={e => setBkTest({ ...bkTest, scheduled_at: e.target.value })} />
                <select data-testid="bookings-test-staff" className={inp}
                  value={bkTest.staff_id} onChange={e => setBkTest({ ...bkTest, staff_id: e.target.value })}>
                  <option value="">Usar staff default</option>
                  {bkStaffs.map(s => {
                    const displayName = s.name
                      || `${s.first_name || ""} ${s.last_name || ""}`.trim()
                      || s.email
                      || s.id
                      || s.staff_id;
                    return (
                      <option key={s.id || s.staff_id} value={s.id || s.staff_id}>{displayName}</option>
                    );
                  })}
                </select>
                <button onClick={testBooking} disabled={bkTesting || !cfg.bookings_service_id}
                  className={`${btn} bg-primary hover:bg-primary text-primary-foreground`}
                  data-testid="bookings-test-btn">
                  {bkTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />} Criar reunião de teste
                </button>
              </div>
              {bkTestResult && (
                <div className={`text-xs rounded-lg p-3 border ${bkTestResult.ok ? "border-[hsl(var(--success))]/25 bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]" : "border-[hsl(var(--danger))]/25 bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]"}`} data-testid="bookings-test-result">
                  {bkTestResult.ok ? (
                    <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Reunião criada · booking_id: <code className="font-mono">{bkTestResult.booking_id}</code></div>
                  ) : (
                    <div className="flex items-center gap-2"><XCircle className="w-4 h-4" /> {bkTestResult.error}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Step 5 — Lead status mapping */}
      <div className={`bg-card border border-border rounded-2xl p-8 space-y-5 ${!hasRefresh ? "opacity-60 pointer-events-none" : ""}`}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-[11px] font-semibold text-foreground">5</div>
          <h3 className="font-ui text-lg">Estado no Zoho (mapa)</h3>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Sempre que uma lead muda de estado na Caring, atualizamos o campo <b>Lead Status</b> no Zoho com o valor correspondente abaixo. Se algum não bater com a vossa picklist do Zoho, edite-o para o valor exato (ex: "Novo", "Convertido"). Deixe em branco para não sincronizar esse estado.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries({
            qualificacao_inicial: "Qualificação Inicial",
            estimativa_enviada: "Estimativa Enviada",
            qt_agendada: "QT Agendada",
            qt_realizada: "QT Realizada",
            proposta_enviada: "Proposta Enviada",
            aceite: "Aceite",
            perdido: "Perdido",
            cancelado: "Cancelado",
          }).map(([k, label]) => (
            <label key={k} className="block" data-testid={`status-map-${k}-row`}>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">{label}</div>
              <input
                data-testid={`status-map-${k}`}
                className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-[#3B82F6]/30"
                placeholder="Valor Zoho (deixe vazio para ignorar)"
                value={(cfg.lead_status_map || {})[k] || ""}
                onChange={(e) => setCfg({ ...cfg, lead_status_map: { ...(cfg.lead_status_map || {}), [k]: e.target.value } })}
              />
            </label>
          ))}
        </div>
        <div className="flex justify-end">
          <button data-testid="status-map-save" onClick={save} disabled={saving} className={`${btn} bg-foreground hover:bg-slate-800 text-background`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar mapa
          </button>
        </div>
      </div>

      {/* Logs */}
      <div className="bg-card border border-border rounded-2xl p-8">
        <div className="flex items-center gap-2 mb-5"><Cable className="w-4 h-4 text-foreground" /><h3 className="font-ui text-lg">Logs de sincronização</h3></div>
        <div className="space-y-2 max-h-[36rem] overflow-y-auto">
          {logs.length === 0 && <div className="text-sm text-muted-foreground/70">Sem logs.</div>}
          {logs.map((l) => {
            const statusColor = l.status === 'success' ? 'text-[#059669]'
              : l.status === 'error' ? 'text-[hsl(var(--danger))]'
              : l.status === 'warning' ? 'text-foreground'
              : 'text-muted-foreground';
            const hasDetails = l.request_payload || l.response;
            return (
              <details key={l.id} className="border border-border rounded-lg p-3 text-xs" data-testid={`zoho-log-${l.id}`}>
                <summary className="flex items-center justify-between mb-1 cursor-pointer list-none">
                  <span className="flex items-center gap-2">
                    <span className={`font-mono font-semibold ${statusColor}`}>{(l.status || '').toUpperCase()}</span>
                    <span className="text-muted-foreground">{l.operation}</span>
                    {l.lead_id && <span className="text-muted-foreground font-mono text-[10px]">lead {String(l.lead_id).slice(0,8)}…</span>}
                  </span>
                  <span className="text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleString('pt-PT') : ''}</span>
                </summary>
                {l.error && <div className="text-[hsl(var(--danger))] mt-2 font-mono text-[11px] whitespace-pre-wrap break-all">{l.error}</div>}
                {hasDetails && (
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {l.request_payload && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-1">Payload enviado</div>
                        <pre className="bg-muted/40 border border-border rounded p-2 text-[10px] whitespace-pre-wrap break-all max-h-64 overflow-auto font-mono text-foreground" data-testid={`zoho-log-payload-${l.id}`}>
{typeof l.request_payload === 'string' ? l.request_payload : JSON.stringify(l.request_payload, null, 2)}
                        </pre>
                      </div>
                    )}
                    {l.response && (
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground mb-1">Resposta bruta do Zoho</div>
                        <pre className="bg-muted/40 border border-border rounded p-2 text-[10px] whitespace-pre-wrap break-all max-h-64 overflow-auto font-mono text-foreground" data-testid={`zoho-log-response-${l.id}`}>
{typeof l.response === 'string' ? l.response : JSON.stringify(l.response, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </details>
            );
          })}
        </div>
      </div>
    </div>
  );
}


/**
 * Admin control to pick which Zoho picklist field holds "Estado do Lead".
 * Fetches all picklist fields on the Leads module and lets the admin persist
 * the api_name. Necessary for tenants that renamed the standard Lead_Status
 * field to a Portuguese label (e.g. "Estado de Cliente Potencial").
 */
function LeadStatusFieldPicker({ cfg, onSaved }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(cfg?.lead_status_api_name || "");
  const [error, setError] = useState("");

  useEffect(() => { setSelected(cfg?.lead_status_api_name || ""); }, [cfg?.lead_status_api_name]);

  const fetchFields = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/zoho/leads/picklist-fields");
      setFields(data.items || []);
      if (!selected && data.current_api_name) setSelected(data.current_api_name);
    } catch (e) {
      const raw = e.response?.data?.detail || "";
      // Detect the very common OAuth scope error and surface a friendly hint.
      if (typeof raw === "string" && raw.toUpperCase().includes("OAUTH_SCOPE_MISMATCH")) {
        setError("Falta o scope OAuth 'ZohoCRM.settings.fields.READ' na autorização actual do Zoho. Volte ao topo desta página, garanta que esse scope está na lista, gere um novo Grant Token no Zoho Console (usando a lista de scopes actualizada) e cole-o no passo 2 para reautorizar. Depois volte aqui.");
      } else {
        setError(raw || "Não foi possível ler os picklists do Zoho.");
      }
    } finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/zoho/leads/status-api-name", { api_name: selected });
      toast.success(`Campo de estado atualizado: ${data.api_name}`);
      onSaved && (await onSaved());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="lead-status-field-picker">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="load-picklists-btn"
          onClick={fetchFields}
          disabled={loading}
          className={`${btn} bg-muted hover:bg-muted text-foreground border border-border`}
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {fields.length ? "Recarregar" : "Carregar picklists do Zoho"}
        </button>
        {fields.length > 0 && (
          <span className="text-[11px] text-muted-foreground">
            {fields.length} campos picklist encontrados no módulo Leads
          </span>
        )}
      </div>

      {error && (
        <div className="text-xs rounded-lg p-3 border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] flex items-start gap-2">
          <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span className="break-all">{error}</span>
        </div>
      )}

      {fields.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-96 overflow-auto pr-1">
            {fields.map(f => (
              <label
                key={f.api_name}
                className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                  selected === f.api_name
                    ? "border-primary bg-muted/40"
                    : "border-border bg-card hover:border-border"
                }`}
                data-testid={`picklist-option-${f.api_name}`}
              >
                <input
                  type="radio"
                  name="lead-status-field"
                  value={f.api_name}
                  checked={selected === f.api_name}
                  onChange={() => setSelected(f.api_name)}
                  className="mt-0.5 accent-[#1D4ED8]"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">{f.field_label || f.api_name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground mb-1">api_name: {f.api_name}</div>
                  {f.sample_values.length > 0 && (
                    <div className="text-[11px] text-muted-foreground">
                      Valores: {f.sample_values.slice(0, 4).join(" · ")}
                      {f.value_count > 4 ? ` … (+${f.value_count - 4})` : ""}
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              data-testid="save-lead-status-api-name"
              onClick={save}
              disabled={saving || !selected}
              className={`${btn} bg-primary hover:bg-[#6F5230] text-primary-foreground disabled:opacity-60`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Guardar campo escolhido
            </button>
            <span className="text-[11px] text-muted-foreground">
              Actual: <span className="font-mono">{cfg?.lead_status_api_name || "Lead_Status"}</span>
            </span>
          </div>
        </>
      )}
    </div>
  );
}



/**
 * Visual report of the /api/zoho/diagnose response. Renders each step
 * with a colored icon (green ✓ / red ✗), any structured data, and the
 * agent-supplied hint when a step fails. Also shows the config summary
 * (client_id preview, refresh_token length, etc.) so the admin can spot
 * missing credentials without opening the browser console.
 */
function DiagnoseReportView({ report }) {
  const cs = report.config_status || {};
  return (
    <div className="mt-3 rounded-xl border border-border bg-muted/40 p-4 space-y-4" data-testid="zoho-diagnose-report">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.15em] text-foreground font-semibold">
          Diagnóstico Zoho · {new Date(report.checked_at).toLocaleTimeString("pt-PT")}
        </div>
        <div className={`text-[11px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${
          report.verdict === "ok"
            ? "bg-[hsl(var(--success-bg))] text-[#059669]"
            : "bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]"
        }`}>
          {report.verdict}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
        <div className="rounded bg-card border border-border p-2">
          <div className="text-[9px] uppercase text-muted-foreground">Client ID</div>
          <div className="font-mono">{cs.client_id_present ? cs.client_id_preview : "— em falta —"}</div>
        </div>
        <div className="rounded bg-card border border-border p-2">
          <div className="text-[9px] uppercase text-muted-foreground">Client Secret</div>
          <div className="font-mono">{cs.client_secret_present ? `${cs.client_secret_length} chars` : "— em falta —"}</div>
        </div>
        <div className="rounded bg-card border border-border p-2">
          <div className="text-[9px] uppercase text-muted-foreground">Refresh Token</div>
          <div className="font-mono">{cs.refresh_token_present ? `${cs.refresh_token_length} chars` : "— em falta —"}</div>
        </div>
        <div className="rounded bg-card border border-border p-2">
          <div className="text-[9px] uppercase text-muted-foreground">Região</div>
          <div className="font-mono">{cs.region}</div>
        </div>
        <div className="rounded bg-card border border-border p-2">
          <div className="text-[9px] uppercase text-muted-foreground">Campo estado (Zoho)</div>
          <div className="font-mono text-[10px] break-all">{cs.lead_status_api_name}</div>
        </div>
        <div className="rounded bg-card border border-border p-2">
          <div className="text-[9px] uppercase text-muted-foreground">Última importação</div>
          <div className="font-mono text-[10px] break-all">
            {cs.last_import_at ? new Date(cs.last_import_at).toLocaleString("pt-PT") : "—"}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {(report.steps || []).map((step, i) => (
          <div key={i} className={`rounded-lg border p-3 text-xs ${
            step.status === "ok"
              ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))]"
              : step.status === "error"
                ? "border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))]"
                : "border-border bg-card"
          }`}>
            <div className="flex items-start gap-2">
              {step.status === "ok" && <CheckCircle2 className="w-4 h-4 text-[#059669] flex-shrink-0 mt-0.5" />}
              {step.status === "error" && <XCircle className="w-4 h-4 text-[hsl(var(--danger))] flex-shrink-0 mt-0.5" />}
              {step.status === "pending" && <Loader2 className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5 animate-spin" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground">{step.name}</div>
                <div className={`mt-1 break-words ${step.status === "error" ? "text-[hsl(var(--danger))]" : "text-muted-foreground"}`}>
                  {step.message}
                </div>
                {step.data && step.data.fields && (
                  <div className="mt-2 text-[10px] font-mono text-muted-foreground break-all">
                    Campos picklist: {step.data.fields.join(", ")}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {report.hint && (
        <div className="rounded-lg border border-[#F0D68C] bg-[hsl(var(--warning-bg))] p-3 text-xs text-[#7B5B08] flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1"><b>Sugestão:</b> {report.hint}</div>
        </div>
      )}

      {report.hint && report.hint.includes("ZohoCRM.settings.fields.READ") && (
        <div className="rounded-lg border border-border bg-card p-3 space-y-2" data-testid="scope-copy-block">
          <div className="text-[10px] uppercase tracking-[0.15em] text-foreground font-semibold">
            Lista de scopes completa (copie e cole no Zoho Console)
          </div>
          <div className="font-mono text-[11px] text-foreground bg-muted rounded p-2 break-all border border-border">
            {SCOPES.join(",")}
          </div>
          <button
            type="button"
            data-testid="diagnose-copy-scopes"
            onClick={() => { navigator.clipboard.writeText(SCOPES.join(",")); toast.success("Lista de scopes copiada — cole no Zoho Console → Self Client → Generate Code"); }}
            className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded bg-primary hover:bg-[#6F5230] text-primary-foreground font-semibold"
          >
            Copiar
          </button>
          <div className="text-[11px] text-muted-foreground leading-relaxed pt-1">
            Passos: (1) Cole isto no Zoho Console → Self Client → separador "Generate Code"; (2) validade 10 min; (3) copie o grant code que aparece; (4) volte cá e cole-o no passo 2 desta página; (5) clique "Trocar por refresh token"; (6) volte a clicar "Diagnosticar Zoho" — o passo 4 deverá agora estar verde.
          </div>
        </div>
      )}
    </div>
  );
}
